// Studby — root component. Routing + auth provider + toaster.
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/AppShell";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import PhotoSolver from "@/pages/PhotoSolver";
import QuizGenerator from "@/pages/QuizGenerator";
import ChatTutor from "@/pages/ChatTutor";
import Subscribe from "@/pages/Subscribe";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

// Wraps a page in the authenticated app shell (top bar + bottom nav).
function Shell({ children }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

// Like Shell, but also requires an active trial/subscription. Users who haven't
// paid yet are sent straight to the plan page (payment right after signup).
function GatedShell({ children }) {
  return (
    <ProtectedRoute>
      <AccessGate>
        <AppShell>{children}</AppShell>
      </AccessGate>
    </ProtectedRoute>
  );
}

function AccessGate({ children }) {
  const { hasAccess } = useAuth();
  if (!hasAccess) return <Navigate to="/app/subscribe" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />

            {/* Stripe result pages (kept behind auth so redirects always land) */}
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />

            {/* Plan page — reachable by any logged-in user (this is the pay gate) */}
            <Route path="/app/subscribe" element={<Shell><Subscribe /></Shell>} />

            {/* Everything else requires an active plan/trial */}
            <Route path="/app/dashboard" element={<GatedShell><Dashboard /></GatedShell>} />
            <Route path="/app/solve" element={<GatedShell><PhotoSolver /></GatedShell>} />
            <Route path="/app/quiz" element={<GatedShell><QuizGenerator /></GatedShell>} />
            <Route path="/app/tutor" element={<GatedShell><ChatTutor /></GatedShell>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </div>
  );
}

export default App;
